import * as React from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ThunkDispatch } from 'redux-thunk';
import { QueryParamsMap } from 'app/RouteDefinition';
import { Action } from 'app/pages/AgentPricing/actionTypes';
import { loadEditDiscountPage, loadViewPage } from 'app/pages/AgentPricing/loadPage';
import PageHeader from 'app/pages/AgentPricing/components/PageHeader';
import TabMenuBar from 'app/pages/AgentPricing/components/TabMenuBar';
import TabPanel from 'app/pages/AgentPricing/components/TabPanel';
import {MainRatesTable} from 'app/pages/AgentPricing/components/RatesTableWidget';
import EditButtonPanel from 'app/pages/AgentPricing/components/EditButtonPanel';
import BackLink from 'app/pages/AgentPricing/components/BackLink';
import ScoolOverrideDialog from 'app/pages/AgentPricing/components/SchoolOverrideDialog';
import EditRatesTableButtonPanel from 'app/pages/AgentPricing/components/EditRatesTableButtonPanel';
import {Alert} from "antd";
import { getIsAgentCommissionBlocked } from 'app/pages/AgentPricing/modules/RefData';

interface StateProps {
    hasNoCommissionGroup?: boolean;
    isAgentStopped?: boolean;
}

function useReloadAgentPricingOnDivisionChange() {
    const dispatch = useDispatch();
    const location = useLocation();
    React.useEffect(() => {
        const reload = () => {
            const qsMap = queryString.parse(location.search || '') as QueryParamsMap;
            const path = location.pathname;
            const d = dispatch as ThunkDispatch<any, unknown, Action>;
            try {
                if (path === '/agents/rates') {
                    void loadViewPage(d, qsMap);
                } else if (path === '/agents/edit-rates') {
                    void loadEditDiscountPage(d, qsMap);
                }
            } catch (err) {
                console.error(err);
            }
        };
        window.addEventListener('selectedDivisionChanged', reload);
        return () => window.removeEventListener('selectedDivisionChanged', reload);
    }, [dispatch, location.pathname, location.search]);
}

function NoCommissionState() {
    return (
        <div style={{ marginTop: 16 }}>
            <Alert
                title="No commission group found"
                description={
                    <span>
                        No commission group has been created for the selected division yet.{' '}
                        <Link to="/commission-category-manager">
                            Please create a commission group first.
                        </Link>
                    </span>
                }
                type="info"
                showIcon
            />
        </div>
    );
}

class ViewPageInternal extends React.Component<StateProps> {
    render() {
        const { hasNoCommissionGroup } = this.props;

        return (
            <div>
                <PageHeader />
                <BackLink />

                {hasNoCommissionGroup ? (
                    <NoCommissionState />
                ) : (
                    <>
                        <TabMenuBar />
                        <TabPanel />
                    </>
                )}
            </div>
        );
    }
}

class EditPageInternal extends React.Component<StateProps> {
    render() {
        const { hasNoCommissionGroup, isAgentStopped: agentStopped } = this.props;

        return (
            <div>
                <PageHeader />

                {hasNoCommissionGroup ? (
                    <NoCommissionState />
                ) : (
                    <>
                        <EditButtonPanel />
                        <MainRatesTable readonly={agentStopped} />
                        {!agentStopped && <EditRatesTableButtonPanel />}
                        {!agentStopped && <ScoolOverrideDialog />}
                    </>
                )}
            </div>
        );
    }
}

function mapStateToProps(state: any): StateProps {
    return {
        hasNoCommissionGroup: state.refData?.hasNoCommissionGroup,
        isAgentStopped: getIsAgentCommissionBlocked(state)
    };
}

const ViewPageWithDivisionReload: React.FC<StateProps> = (props) => {
    useReloadAgentPricingOnDivisionChange();
    return <ViewPageInternal {...props} />;
};

const EditPageWithDivisionReload: React.FC<StateProps> = (props) => {
    useReloadAgentPricingOnDivisionChange();
    return <EditPageInternal {...props} />;
};

export const ViewPage = connect(mapStateToProps)(ViewPageWithDivisionReload);
export const EditPage = connect(mapStateToProps)(EditPageWithDivisionReload);
